import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Header, Overlay } from "./components";
import AuthModal from "./components/Auth";

import jwtDecode from "jwt-decode";
import ls from "localstorage-slim";

import {
  Route,
  Routes,
  useLocation,
  matchPath,
  useNavigate,
} from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import AdminPage from "./pages/AdminPage";
import AppContext from "./AppContext";
import OnboardPage from "./pages/Onboard";

import { PAGE_STATE, menus } from "./constants";
import "./styles/app.scss";
import useUsers from "./hooks/useUsers";
import ProfilePage from "./pages/ProfilePage";
import ChangePasswordPage from "./pages/ChangePasswordPage";

import ChatWindow from "./components/ChatWindow";
import ChatPage from "./pages/ChatPage";
import SupportPage from "./pages/Support";
import LinePage from "./pages/Line";
import ChatSessionContext, {
  ChatSessionProvider,
} from "./contexts/ChatSessionContext";
import WorkExchangeContext from "./contexts/WorkExchangeContext";
import Footer from "./components/Home/Footer";
import NavBar from "./components/common/Header/NavBar";
import ProjectView from "./components/ProjectView";
import ArtDesignPage from "./pages/ArtDesign";
import useChat from "./hooks/useChat";
import _ from "lodash";
import CommissionPage from "./pages/CommissionPage";
import CompanyPage from "./pages/CompanyPage";
import InvitationPage from "./pages/InvitationPage";
import ShoppingPage from "./pages/ShoppingPage";
import CommissionContext from "./contexts/CommissionContext";
import ShoppingContext from "./contexts/ShoppingContext";
import { toast } from "react-toastify";
import ErrorBoundary from "./ErrorBoundary";
import NotificationPage from "./pages/NotificationPage";
import useLikes from "./hooks/useLikes";
import useLangs from "./hooks/useLangs";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import BlogPage from "./pages/BlogPage/index.jsx";
import WorkExchangeView from "./components/WorkExchange/View";
import TenderingPage from "./pages/TenderingPage/index.jsx";
import ChatBubbles from "./components/common/Chat/Bubbles.jsx";
import NewProject from "./components/NewProject/index.jsx";
import ChangePlanPage from "./pages/ChangePlan/index.jsx";
import CartProvider from "./contexts/CartContext.jsx";
import { clarity } from "react-microsoft-clarity";
import { ReactComponent as CloseSvg } from "./assets/projectView/close.svg";
import { SocketContext } from "./contexts/SocketContext.jsx";
import previousImg from "./assets/home/previous.png";
import nextImg from "./assets/home/next.png";
import MissingPage from "./pages/404Page/index.jsx";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import useAuth from "./hooks/useAuth.js";
import Confirm from "./components/Confirm/index.jsx";
import LocationSharer from "./components/LocationSharer/index.jsx";
import WebGLLimitations from "./pages/WebGLPage/index.jsx";
import { differenceInDays } from "date-fns";
import TrialUpgradePage from "./pages/TrialUpgrade/index.jsx";
import TrialUpgrade from "./components/TrialUpgrade/index.jsx";
import withClearCache from "./withClearCache.jsx";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/swiper-bundle.css";
import "swiper/css/navigation";
import NDA from "./components/Tendering/NDA.jsx";
import PaymentCompletePage from "./pages/PaymentCompletePage/index.jsx";

const omisePublicKey = import.meta.env.OMISE_PUBLIC_KEY;
const omiseSecretKey = import.meta.env.OMISE_SECRET_KEY;
const googleClientId = import.meta.env.GOOGLE_CLIENT_ID;
const clarityKey = import.meta.env.MICROSOFT_CLARITY_ID;
const assetsUri = import.meta.env.ASSETS_URI;

const App = () => {
  const { socket, connect, disconnect } = useContext(SocketContext);
  const { pathname } = useLocation();

  const [user, setUser] = useState();
  const [company, setCompany] = useState();
  const [state, setState] = useState(PAGE_STATE.LOADING);
  const [token, setToken] = useState({
    accessToken: localStorage.getItem("token"),
    refreshToken: localStorage.getItem("token"),
  });
  const [isAuth, setIsAuth] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(matchPath("/admin/*", pathname));
  const [toggleChat, setToggleChat] = useState(false);
  const [isChatPage, setIsChatPage] = useState(matchPath("/chats", pathname));
  const {
    getUser,
    getStaff,
    queryDesignWorks,
    queryDesignSpaces,
    queryDesignStyles,
    queryWorkExchanges,
    queryPlans,
    queryUsers,
    getUserPermissions,
    getCompany,
    queryItems,
    queryWorkExchangeModelTypes,
    queryWorkExchangeMaterialTypes,
    queryWorkExchangeDrawingTypes,
    queryFaqCategories,
    queryFaqSubCategories,
    queryFaqs,
  } = useUsers();
  const { signInExternal, refreshToken } = useAuth();
  const navigate = useNavigate();
  const [socketConnected, setSocketConnected] = useState(false);

  const [toggleNavbar, setToggleNavBar] = useState(false);
  const [displaySignInModal, setDisplaySignInModal] = useState(false);
  const [signInRedrect, setSignInRedrect] = useState(undefined);
  const [projectViewing, setProjectViewing] = useState(undefined);
  const [designStyles, setDesignStyles] = useState(undefined);
  const [plans, setPlans] = useState(undefined);
  const [faqs, setFaqs] = useState(undefined);
  const [commissions, setCommissions] = useState(undefined);

  const [shoppingFilter, setShoppingFilter] = useState({
    sort: { createdOn: -1 },
  });
  const [workExchangeFilter, setWorkExchangeFilter] = useState({
    currentSelect: undefined,
  });
  const { queryLikes } = useLikes();
  const { getLangs } = useLangs();
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");

  const { createLike, deleteLike } = useLikes();
  const location = useLocation();
  const previousLocation = location.state?.previousLocation;
  const chatContext = useContext(ChatSessionContext);
  const [isToggleImagesPreview, setIsToggleImagesPreview] = useState(undefined);
  const [gsiInit, setGSIInit] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(undefined);
  const [shareLocationDetail, setShareLocationDetail] = useState(undefined);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [isTrialEnded, setIsTrialEnded] = useState(false);
  const [isTenderingNDAPopup, setIsTenderingNDAPopup] = useState(undefined);

  useEffect(() => {
    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleSignInResponse,
      });

      setGSIInit(true);
    } catch (error) {
      console.error(error);
    }

    clarity.init(clarityKey);
    clarity.consent();

    console.log("Clarity status:", clarity.hasStarted());

    Omise.setPublicKey(omisePublicKey);

    let _faqs = ls.get("urbaan_faqs");
    let _designStyles = ls.get("urbaan_design_styles");
    let _plans = ls.get("urbaan_design_plans");

    if (_faqs && _designStyles && _plans) {
      setFaqs(_faqs);
      setDesignStyles(_designStyles);
      setPlans(_plans);
    }

    if (!_faqs || !_designStyles || !_plans) {
      // * Get Master Data to store for later use throughout the platform
      // queryDesignWorks, queryDesignSpaces, queryDesignStyles
      const promises = [
        queryDesignWorks({}, undefined, -1),
        queryDesignSpaces({}, undefined, -1),
        queryDesignStyles({}, undefined, -1),
        queryPlans(),
        queryWorkExchanges({}, undefined, -1),
        queryWorkExchangeModelTypes({}, undefined, -1),
        queryWorkExchangeMaterialTypes({}, undefined, -1),
        queryWorkExchangeDrawingTypes({}, undefined, -1),
        queryFaqCategories({}, undefined, -1),
        queryFaqSubCategories({}, undefined, -1),
        queryFaqs({}, undefined, -1),
      ];

      Promise.allSettled(promises).then(
        ([
          workResult,
          spaceResult,
          styleResult,
          planResult,
          workExchangeResult,
          workExchangeModelResult,
          workExchangeMaterialResult,
          workExchangeDrawingResult,
          faqCategoriesResult,
          faqSubCategoriesResult,
          faqResult,
        ]) => {
          if (
            workResult.status !== "fulfilled" ||
            spaceResult.status !== "fulfilled" ||
            styleResult.status !== "fulfilled" ||
            workExchangeResult.status !== "fulfilled" ||
            workExchangeModelResult.status !== "fulfilled" ||
            workExchangeMaterialResult.status !== "fulfilled" ||
            workExchangeDrawingResult.status !== "fulfilled" ||
            faqCategoriesResult.status !== "fulfilled" ||
            faqSubCategoriesResult.status !== "fulfilled"
          ) {
            console.error("works error", workResult);
            console.error("spaces error", spaceResult);
            console.error("styles error", styleResult);
            console.error("workExchange error", workExchangeResult);
            console.error("workExchange Model error", workExchangeModelResult);
            console.error(
              "workExchange Material error",
              workExchangeMaterialResult
            );
            console.error(
              "workExchange Drawing error",
              workExchangeDrawingResult
            );
            console.error("FAQ Category error", faqCategoriesResult);
            console.error("FAQ  Sub-Category error", faqSubCategoriesResult);
            console.error("FAQ error", faqResult);
            return;
          }

          _faqs = {
            category: faqCategoriesResult.value.data.map((d) => ({
              name: d.name,
              value: d._id,
              icon: d.icon,
              route: d.route,
            })),
            subCategory: faqSubCategoriesResult.value.data.map((d) => ({
              name: d.name,
              value: d._id,
              category: d.category,
              route: d.route,
              icon: d.icon,
            })),
            content: faqResult.value.data.map((d) => ({ ...d })),
          };

          _designStyles = {
            works: workResult.value.data.map((d) => ({
              name: d.name,
              value: d._id,
              projects: d.projects ?? 0,
            })),
            spaces: spaceResult.value.data.map((d) => ({
              name: d.name,
              value: d._id,
              projects: d.projects ?? 0,
            })),
            styles: styleResult.value.data.map((d) => ({
              name: d.name,
              value: d._id,
              projects: d.projects ?? 0,
            })),
            workExchanges: workExchangeResult.value.data.map((d) => ({
              name: d.name,
              value: d._id,
              projects: d.projects ?? 0,
              fileTypes: d.fileTypes,
              requireProgram: d.requireProgram,
              requireRenderer: d.requireRenderer,
              requireMaterials: d.requireMaterials,
              categories: d.categories,
            })),
            workExchangeModelTypes: workExchangeModelResult.value.data.map(
              (d) => ({
                name: d.name,
                value: d._id,
                projects: d.projects ?? 0,
              })
            ),
            workExchangeMaterialTypes:
              workExchangeMaterialResult.value.data.map((d) => ({
                name: d.name,
                value: d._id,
                projects: d.projects ?? 0,
              })),
            workExchangeDrawingTypes: workExchangeDrawingResult.value.data.map(
              (d) => ({
                name: d.name,
                value: d._id,
                projects: d.projects ?? 0,
              })
            ),
          };

          _plans = planResult.value.data;

          ls.set("urbaan_faqs", _faqs, { ttl: 86400 });
          ls.set("urbaan_design_styles", _designStyles, {
            ttl: 86400,
          });
          ls.set("urbaan_plans", _plans, { ttl: 86400 });

          setFaqs(_faqs);
          setDesignStyles(_designStyles);
          setPlans(_plans);
        }
      );
    }

    setPageState(PAGE_STATE.IDLE);

    // * Prevent scroll wheel effecting number inputs
    document.addEventListener("wheel", function (event) {
      if (document.activeElement.type === "number") {
        document.activeElement.blur();
      }
    });
  }, []);

  useEffect(() => {
    if (
      pageState !== PAGE_STATE.IDLE ||
      _.isEmpty(plans) ||
      _.isEmpty(designStyles)
    )
      return;

    if (isAuth && token) return;

    handleTokenChanged(token);

    return () => {};
  }, [plans, designStyles, pageState]);

  // FIXME: Handle google
  const handleGoogleSignInResponse = async (response) => {
    const { credential } = response;

    const { picture, sub: googleId } = jwtDecode(credential);

    try {
      const findUser = await queryUsers({
        googleId: googleId,
      });

      if (findUser.data.length === 0) {
        localStorage.setItem(
          "onboard:credentials",
          JSON.stringify({
            googleId: googleId,
            googleAvatar: picture,
          })
        );
        localStorage.setItem("onboard:registration-id", "google");
        onClose();
        navigate("onboard");
        return;
      }

      const token = await signInExternal(googleId, "google");
      handleTokenChanged(token);
    } catch (error) {
      toast.error(`Unable to sign in google`);
    }
  };

  useEffect(() => {
    if (!socket || _.isEmpty(designStyles)) return;

    socket.on("master-changed:design-types", (payload) => {
      if (!payload) {
        return;
      }

      const { types, inc } = payload;

      setDesignStyles((current) => {
        // * works, spaces, styles
        current.works = current.works.map((w) => {
          if (!types.includes(w.value)) return w;
          w.projects += inc;
          return w;
        });
        current.spaces = current.spaces.map((w) => {
          if (!types.includes(w.value)) return w;
          w.projects += inc;
          return w;
        });
        current.styles = current.styles.map((w) => {
          if (!types.includes(w.value)) return w;
          w.projects += inc;
          return w;
        });
        return current;
      });
    });

    socket.on("master-changed:work-exchange", (payload) => {
      if (!payload) {
        return;
      }

      const { types, inc } = payload;

      setDesignStyles((current) => {
        // * works, spaces, styles
        current.workExchanges = current.workExchanges.map((w) => {
          if (!types.includes(w.value)) return w;

          w.projects += inc;
          return w;
        });

        return current;
      });
    });

    socket.on("master-changed:work-exchange-types", (payload) => {
      if (!payload) {
        return;
      }

      const { types, inc } = payload;

      setDesignStyles((current) => {
        // * works, spaces, styles
        current.workExchangeModelTypes = current.workExchangeModelTypes.map(
          (w) => {
            if (!types.includes(w.value)) return w;
            w.projects += inc;
            return w;
          }
        );

        current.workExchangeDrawingTypes = current.workExchangeDrawingTypes.map(
          (w) => {
            if (!types.includes(w.value)) return w;
            w.projects += inc;
            return w;
          }
        );

        current.workExchangeMaterialTypes =
          current.workExchangeMaterialTypes.map((w) => {
            if (!types.includes(w.value)) return w;
            w.projects += inc;
            return w;
          });
        return current;
      });
    });

    return () => {
      // socket.removeAllListeners("master-changed:design-types");
      // socket.removeAllListeners("master-changed:work-exchange");
      // socket.removeAllListeners("master-changed:work-exchange-types");
    };
  }, [socket, designStyles]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (
      !chatContext.currentSession ||
      isChatPage ||
      !chatContext.currentSession.endUser
    )
      return;
    setToggleChat(true);
  }, [chatContext.currentSession]);

  // * Detect routes
  useEffect(() => {
    const isMatchChatPage = matchPath("/chats", pathname);
    if (isMatchChatPage && !isChatPage) {
      setIsChatPage(true);
    }

    if (!isMatchChatPage && isChatPage) {
      setIsChatPage(false);
    }

    return () => {};
  }, [pathname]);

  useEffect(() => {
    initLangs(lang);
  }, [lang]);

  useEffect(() => {
    if (isAdmin || !isAuth) {
      return;
    }

    navigate(signInRedrect);
    setSignInRedrect(undefined);
  }, [isAuth]);

  useEffect(() => {
    if (!user) return;

    if (user.planDetail?.price === 0) {
      return;
    }

    // * Check if trial exists and has ended
    // * Case: Not in company
    if (!user.company && user.trialEndOn) {
      const trialEndsIn = differenceInDays(
        new Date(user.trialEndOn),
        new Date()
      );

      setIsTrialEnded(trialEndsIn <= 0);
    }
  }, [user]);

  useEffect(() => {
    if (!gsiInit) {
      return;
    }

    if (isAuth || isAuth === undefined) {
      return;
    }

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed()) {
        console.error("Try to clear the cookies or try again later!");
      }
    });
  }, [isAuth, gsiInit]);

  const initLangs = async (key) => {
    try {
      await i18n.use(initReactI18next).init({
        resources: {},
        lng: key,
        interpolation: {
          escapeValue: false,
        },
      });
      if (!key) return;
      const languages = await getLangs(key);
      await i18n.use(initReactI18next).init({
        resources: languages,
        lng: key,
        interpolation: {
          escapeValue: false,
        },
      });
    } catch (error) {
      toast.error(`Unable to init language`);
    }
    localStorage.setItem("lang", key);
    i18n.changeLanguage(key);
  };

  const switchLang = (target) => {
    setLang(target);
  };

  const getLang = (component) => {
    try {
      const { t } = useTranslation(component);
      return t;
    } catch (error) {
      toast.error(`Unable to get language`);
    }
  };

  const handleViewProject = (workId, _location) => {
    setProjectViewing(workId);

    navigate(`/projects/${workId}`, {
      state: { previousLocation: _location ?? location },
    });
  };

  const handleProjectViewClose = () => {
    setProjectViewing(undefined);
    navigate(-1);
  };

  const handleSignIn = () => {
    handleToggleAuthModal(true, "signin");
  };

  const handleLogout = () => {
    console.log("token removed");

    localStorage.removeItem("token");
    disconnect();
    setUser(undefined);
    setCompany(undefined);
    setIsAuth(false);
    setToken(undefined);
  };

  const handleToggleAuthModal = (toggle, mode, redirect) => {
    // * Close auth modal
    if (!toggle) {
      setDisplaySignInModal(false);
      return;
    }

    setSignInRedrect(redirect);
    setDisplaySignInModal(true);
  };

  /**
   * To handle and check token when set
   * @param {*} accessToken
   * @returns
   */
  const handleTokenChanged = async (token) => {
    const { accessToken: _accessToken, refreshToken: _refreshToken } =
      token ?? {};

    if (!_accessToken) {
      console.log("token removed 2");
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("rememberMe");
      setIsAuth(false);
      setToken(null);
      setState(PAGE_STATE.IDLE);
      setSocketConnected(false);
      return;
    }

    // * Check if token is valid
    const { uid, exp } = jwtDecode(_accessToken);

    // * Check if expired
    const _expired = new Date() > new Date(exp * 1000);

    if (!_expired) {
      localStorage.setItem("token", _accessToken);

      if (localStorage.getItem("rememberMe") === "true") {
        localStorage.setItem("refreshToken", _refreshToken);
      }
    }

    // * Attempt refreshing token if present
    if (_expired && _refreshToken) {
      try {
        const _newTokens = await refreshToken();
        localStorage.setItem("token", _newTokens.accessToken);
        localStorage.setItem("refreshToken", _newTokens.refreshToken);
      } catch (error) {
        console.log("token removed 3");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("rememberMe");
        setIsAuth(false);
        setToken(null);
        setState(PAGE_STATE.IDLE);
        setSocketConnected(false);
        return;
      }
    }

    if (_expired && !_refreshToken) {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("rememberMe");
      setIsAuth(false);
      setToken(null);
      setState(PAGE_STATE.IDLE);
      setSocketConnected(false);
      return;
    }

    // * Handle Admin
    if (isAdmin) {
      try {
        const _staff = await getStaff(uid);
        setUser({ ..._staff, support: true });
        setCompany(undefined);
        setToken(_accessToken);
        setIsAuth(true);
        setState(PAGE_STATE.IDLE);
        return connect(_accessToken, _staff._id, "support");
      } catch (error) {
        console.error("error admin", error);

        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("rememberMe");
        setIsAuth(false);
        setToken(null);
        setState(PAGE_STATE.IDLE);
        setSocketConnected(false);
        setCompany(undefined);
      }

      return;
    }

    try {
      let _user = await getUser(uid);

      // * Get plan
      const _plan = plans?.find((p) => p._id === _user.plan);

      _user.planDetail = _plan;

      // * Get company data if exists
      if (_user.company) {
        const _company = await getCompany(_user.company);
        setCompany(_company);
      }

      const { data: likes } = await queryLikes({ user: uid });
      if (likes.length !== 0) {
        _user.likes = likes[0].likes;
      }

      // * Get permission
      const _permissions = [];

      for (const permission of await getUserPermissions(uid)) {
        for (const verb of permission.verbs) {
          _permissions.push({
            resource: permission.code,
            verb,
          });
        }
      }

      _user.permissions = _permissions;

      setUser(_user);
      setToken(_accessToken);
      setIsAuth(true);
      setState(PAGE_STATE.IDLE);
      connect(_accessToken, _user._id, "user", _user.company);

      if (
        pathname.includes("change-password") ||
        pathname.includes("onboard") ||
        pathname.includes("line")
      ) {
        navigate("/");
      }
    } catch (error) {
      console.error("token removed 4", error);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("rememberMe");
      setIsAuth(false);
      setToken(null);
      setState(PAGE_STATE.IDLE);
      setSocketConnected(false);
    }
  };

  const handleMaximizeChat = () => {
    setToggleChat(false);
    navigate("/chats");
  };

  const handleToggleNavbar = () => {
    setToggleNavBar((curr) => !curr);
  };

  const handleViewTicket = (ticketId) => {
    setToggleChat(false);
    navigate(`/support/tickets/${ticketId}`);
  };

  const handleViewProfile = async (userId, view) => {
    if (!userId) {
      return;
    }

    if (userId === user?._id && !view) {
      navigate(`/profile`);
      return;
    }

    if (userId === user?._id && view) {
      navigate(`/profile?view=guest`);
      return;
    }

    // * Get link to profile
    const { data: users } = await queryUsers({ _id: userId }, 1, 1, {
      uri: 1,
    });

    if (users.length === 0) {
      toast.error("User not found");
      return;
    }

    navigate(`/${users[0].uri}`);
  };

  const handleViewCompany = async (companyId) => {
    navigate(`/companies/${companyId}`);
  };

  const handleResetFilterShopping = () => {
    setShoppingFilter({
      sort: { createdOn: -1 },
    });
  };

  const handleAddLikes = async (workId) => {
    if (!isAuth) {
      handleToggleAuthModal(true, "signin");
      return false;
    }

    try {
      await createLike(user._id, { work: workId });

      setUser((curr) => {
        let _likes = _.cloneDeep(curr?.likes ?? []);
        _likes.push(workId);

        return {
          ...curr,
          likes: _likes,
        };
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleRemoveLikes = async (workId) => {
    if (!isAuth) return;

    await deleteLike(user._id, workId);

    setUser((curr) => {
      let _likes = _.cloneDeep(curr?.likes ?? []).filter((w) => w !== workId);

      return {
        ...curr,
        likes: _likes,
      };
    });
  };

  const handleCancelNewProject = () => {
    if (user?.company) {
      navigate(`/companies/${user.company}`);
      return;
    }

    navigate("/profile");
  };

  const handleNewProjectSuccess = (workId) => {
    if (user?.company) {
      navigate(`/companies/${user.company}`);
      return;
    }

    navigate("/profile");
  };

  const handleCheckPermission = (resource, verb) => {
    const _found = user?.permissions.find(
      (p) => p.resource === resource && p.verb === verb
    );

    if (_found) return true;

    return false;
  };

  const _handletoggleChatWindow = () => {
    chatContext.setIsSupport(false);
    setToggleChat(true);
  };

  const handleToggleChat = (toggle, isSupport) => {
    setToggleChat(!toggleChat);
  };

  const _handleDisplayImages = (images, startingIndex = 0) => {
    setIsToggleImagesPreview({ images, startingIndex });
  };

  const handleDisplayConfirm = (title, desc, actions, onActionClick) => {
    setConfirmDetails({
      title,
      desc,
      actions,
      onActionClick,
    });
  };

  const handleShareLocation = (onClose, onShare) => {
    setShareLocationDetail({
      onClose,
      onShare,
    });
  };

  const handleRefetchUser = async () => {
    if (!isAuth || !token) return;

    try {
      const uid = user._id;

      let _user = await getUser(uid);

      // * Get plan
      const _plan = plans?.find((p) => p._id === _user.plan);

      _user.planDetail = _plan;

      // * Get company data if exists
      if (_user.company) {
        const _company = await getCompany(_user.company);
        setCompany(_company);
      }

      const { data: likes } = await queryLikes({ user: uid });
      if (likes.length !== 0) {
        _user.likes = likes[0].likes;
      }

      // * Get permission
      const _permissions = [];

      for (const permission of await getUserPermissions(uid)) {
        for (const verb of permission.verbs) {
          _permissions.push({
            resource: permission.code,
            verb,
          });
        }
      }

      _user.permissions = _permissions;

      setUser(_user);
    } catch (error) {
      alert("Error refetching user", error);
    }
  };

  const handleTenderingViewNDA = (tenderingId) => {
    if (!tenderingId) return;

    setIsTenderingNDAPopup(tenderingId);
  };

  const appContextValues = useMemo(
    () => ({
      lang,
      setLang: switchLang,
      getLang,
      isAuth,
      isAdmin,
      token,
      user,
      setUser,
      toggleAuthModal: handleToggleAuthModal,
      setToken: handleTokenChanged,
      setIsAdmin: setIsAdmin,
      toggleNavbar: handleToggleNavbar,
      viewProject: handleViewProject,
      designStyles,
      plans,
      faqs,
      viewTicket: handleViewTicket,
      viewProfile: handleViewProfile,
      addLikes: handleAddLikes,
      removeLikes: handleRemoveLikes,
      viewCompany: handleViewCompany,
      isAllowed: handleCheckPermission,
      projectViewing,
      toggleChatWindow: _handletoggleChatWindow,
      company,
      displayImages: _handleDisplayImages,
      confirm: handleDisplayConfirm,
      shareLocation: handleShareLocation,
      refetchUser: handleRefetchUser,
      isTrialEnded,
      setIsTrialEnded,
      tenderingViewNDA: handleTenderingViewNDA,
    }),
    [
      isAuth,
      token,
      isAdmin,
      user,
      designStyles,
      plans,
      lang,
      projectViewing,
      company,
      isTrialEnded,
    ]
  );

  const commissionContextValues = useMemo(
    () => ({
      commissions,
      setCommissions,
    }),
    [commissions]
  );

  const shoppingContextValues = useMemo(
    () => ({
      shoppingFilter,
      setShoppingFilter,
      resetShoppingFilter: handleResetFilterShopping,
    }),
    [shoppingFilter]
  );

  const WorkExchangeContextValues = useMemo(
    () => ({
      workExchangeFilter,
      setWorkExchangeFilter,
    }),
    [workExchangeFilter]
  );

  const displayImagePreview = () => {
    const { images, startingIndex } = isToggleImagesPreview;

    return (
      <Overlay onClose={() => setIsToggleImagesPreview(undefined)}>
        <div className="images-preview">
          <div
            className="images-preview-close"
            onClick={() => {
              setIsToggleImagesPreview(undefined);
            }}
          >
            <CloseSvg />
            Close
          </div>
          <div className="images-preview-content">
            <img
              className="images-preview-slider-left"
              src={previousImg}
              width={48}
            />
            <Swiper
              className="images-preview-slider"
              modules={[Navigation, Pagination]}
              spaceBetween={0}
              slidesPerView={1}
              navigation={{
                enabled: true,
                prevEl: ".images-preview-slider-left",
                nextEl: ".images-preview-slider-right",
              }}
              loop={true}
              pagination={{
                el: ".snp-pagination",
                type: "fraction",
                clickable: true,
                renderBullet: (index, className) => {
                  return <span className={className}>{index + 1}</span>;
                },
              }}
              initialSlide={startingIndex ?? 0}
            >
              {images.map((image, index) => {
                const getImageUri = (path) => {
                  if (path.startsWith("blob:")) {
                    return path;
                  }

                  if (path.startsWith(assetsUri)) {
                    return path;
                  }

                  return `${assetsUri}/${path}`;
                };

                return (
                  <SwiperSlide className="images-preview-slider-slide">
                    <img
                      className="images-preview-slider-slide__image"
                      src={getImageUri(image)}
                      onError={({ target }) => {
                        target.src = image;
                      }}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <img
              className="images-preview-slider-right"
              src={nextImg}
              width={48}
            />
          </div>
        </div>
      </Overlay>
    );
  };

  if (state === PAGE_STATE.LOADING) {
    return <></>;
  }

  if (isAdmin) {
    return (
      <AppContext.Provider value={appContextValues}>
        <CommissionContext.Provider value={commissionContextValues}>
          <ChatSessionProvider>
            <div className="urbaan">
              <Routes>
                <Route path="admin/*" element={<AdminPage />} />
              </Routes>
            </div>
            {isToggleImagesPreview && displayImagePreview()}
          </ChatSessionProvider>
        </CommissionContext.Provider>
      </AppContext.Provider>
    );
  }

  return (
    <AppContext.Provider value={appContextValues}>
      <ChatSessionProvider>
        <CartProvider>
          <ShoppingContext.Provider value={shoppingContextValues}>
            <CommissionContext.Provider value={commissionContextValues}>
              <WorkExchangeContext.Provider value={WorkExchangeContextValues}>
                <div id="urbaan">
                  <Header
                    user={user}
                    isAuth={isAuth}
                    menus={menus}
                    onLogin={handleSignIn}
                    onLogout={handleLogout}
                    onNavigateTo={(path) => navigate(path)}
                  />
                  <div className="urbaan-content">
                    <Routes location={previousLocation || location}>
                      <Route path="/">
                        <Route path="" element={<LandingPage />} />
                        <Route path="webgl" element={<WebGLLimitations />} />
                        <Route path="companies/*" element={<CompanyPage />} />
                        <Route path="onboard" element={<OnboardPage />} />
                        <Route
                          path="change-plan"
                          element={<ChangePlanPage />}
                        />
                        <Route
                          path="change-password"
                          element={<ChangePasswordPage />}
                        />
                        <Route path="chats" element={<ChatPage />} />
                        <Route path="support/*" element={<SupportPage />} />
                        <Route path="line/*" element={<LinePage />} />
                        <Route
                          path="art-designs/*"
                          element={<ArtDesignPage />}
                        />
                        <Route
                          path="commissions"
                          element={<CommissionPage />}
                        />
                        <Route
                          path="invitation/*"
                          element={<InvitationPage />}
                        />
                        <Route
                          path="shopping"
                          element={<ShoppingPage isParanoma={false} />}
                        />
                        <Route
                          path="notification"
                          element={<NotificationPage />}
                        />
                        <Route path="blogs/*" element={<BlogPage />} />
                        <Route
                          path="tenderings/*"
                          element={<TenderingPage />}
                        />
                        <Route
                          path="work-exchanges/:id"
                          element={<WorkExchangeView />}
                        />
                        <Route
                          path="new-project"
                          element={
                            <NewProject
                              onCancel={handleCancelNewProject}
                              onSuccess={handleNewProjectSuccess}
                            />
                          }
                        />
                        <Route path="404" element={<MissingPage />} />
                        <Route
                          path="payments/completed"
                          element={<PaymentCompletePage />}
                        />
                        <Route path="*" element={<ProfilePage />} />
                        {!previousLocation && (
                          <Route
                            path="/projects/:id"
                            element={
                              <ProjectView
                                onClose={() => {
                                  setProjectViewing(undefined);
                                  navigate("/");
                                }}
                              />
                            }
                          />
                        )}
                      </Route>
                    </Routes>
                    {previousLocation && (
                      <Routes>
                        <Route
                          path="/projects/:id"
                          element={
                            <ProjectView onClose={handleProjectViewClose} />
                          }
                        />
                      </Routes>
                    )}
                  </div>
                  <Footer />
                  {toggleNavbar && <NavBar />}
                  {displaySignInModal && <AuthModal redirect={signInRedrect} />}
                  {isAuth && !isChatPage && (
                    <div className="chat-section">
                      {toggleChat ? (
                        <ChatWindow
                          userId={user._id}
                          mini={true}
                          onClose={() => handleToggleChat(false)}
                          onMaximize={handleMaximizeChat}
                        />
                      ) : (
                        <ChatBubbles />
                      )}
                    </div>
                  )}
                </div>
                {isToggleImagesPreview && displayImagePreview()}
                {confirmDetails && (
                  <Confirm
                    {...confirmDetails}
                    onClose={() => setConfirmDetails(undefined)}
                  />
                )}
                {shareLocationDetail && (
                  <LocationSharer
                    {...shareLocationDetail}
                    onClose={() => setShareLocationDetail(undefined)}
                  />
                )}
                {isTrialEnded &&
                  !location.pathname.includes("/trial-upgrade") &&
                  !location.pathname.includes("/change-plan") && (
                    <TrialUpgrade />
                  )}
                {isTenderingNDAPopup && (
                  <NDA
                    tenderingId={isTenderingNDAPopup}
                    onClose={() => setIsTenderingNDAPopup(undefined)}
                  />
                )}
              </WorkExchangeContext.Provider>
            </CommissionContext.Provider>
          </ShoppingContext.Provider>
        </CartProvider>
      </ChatSessionProvider>
    </AppContext.Provider>
  );
};

export default withClearCache(App);
